/**
 * Frontend Module Gating Helper
 * =============================
 * Mirrors backend logic to determine module enablement.
 * Used for UI gating (sidebar, routes, components).
 */

import { isAccountingClient } from './tenantType';

/**
 * Get default enabledModules for a tenantType (mirrors backend)
 * @param {string} tenantType - 'accounting_firm', 'mrwd', 'restaurant', 'retail'
 * @returns {Object} enabledModules with booleans for each module
 */
export function getDefaultModulesByTenantType(tenantType) {
  const base = {
    accountingCore: true,
    accountingReports: true,
    birBooks: false,
    tax: false,
    vat: false,
    ar: false,
    ap: false,
    invoicing: false,
    billing: false,
    pos: false,
    inventory: false,
    attendance: false
  };

  switch (tenantType) {
    case 'accounting_firm':
    case 'mrwd':
      return {
        ...base,
        accountingCore: true,
        accountingReports: true,
        birBooks: true,
        tax: true,
        pos: false,
        inventory: false,
        attendance: false
      };

    case 'restaurant':
      return {
        ...base,
        accountingCore: true,
        accountingReports: true,
        pos: true,
        inventory: true,
        attendance: true,
        birBooks: false,
        tax: false
      };

    case 'retail':
      return {
        ...base,
        accountingCore: true,
        accountingReports: true,
        pos: false,
        inventory: false,
        attendance: false
      };

    case 'billing':
      return {
        ...base,
        accountingCore: true,
        accountingReports: true,
        birBooks: false,
        tax: false,
        pos: false,
        inventory: false,
        attendance: false,
        billing: true
      };

    default:
      return base;
  }
}

/**
 * Check if a tenant has a specific module enabled
 * Reads from tenant.enabledModules if available, falls back to defaults
 * @param {Object} tenant - Tenant object from /api/me or context
 * @param {string} moduleKey - Module to check (e.g., 'accountingReports', 'pos')
 * @returns {boolean} true if module is enabled
 */
export function hasModule(tenant, moduleKey) {
  if (!tenant) return false;

  // If enabledModules exists and has the key, use it
  if (tenant.enabledModules && typeof tenant.enabledModules[moduleKey] === 'boolean') {
    return tenant.enabledModules[moduleKey];
  }

  // Fallback to defaults based on tenantType
  const defaults = getDefaultModulesByTenantType(tenant.tenantType);
  return defaults[moduleKey] || false;
}

/**
 * Check if a tenant is an accounting firm
 * @param {Object} tenant
 * @returns {boolean}
 */
export function isAccountingFirm(tenant) {
  return isAccountingClient(tenant?.tenantType);
}

/**
 * Check if a tenant is a restaurant
 * @param {Object} tenant
 * @returns {boolean}
 */
export function isRestaurant(tenant) {
  return tenant?.tenantType === 'restaurant';
}

/**
 * Check if a tenant is retail
 * @param {Object} tenant
 * @returns {boolean}
 */
export function isRetail(tenant) {
  return tenant?.tenantType === 'retail';
}
/**
 * Get list of modules that are dependent on accountingCore
 * These modules should be disabled if accountingCore is disabled
 * @returns {Array<string>} List of dependent module keys
 */
export function getAccountingCoreDependencies() {
  return [
    'accountingReports',
    'birBooks',
    'tax',
    'vat',
    'ar',
    'ap',
    'invoicing',
    'billing'
  ];
}

/**
 * Get list of modules that are dependent on POS
 * These are optional dependencies that CAN be disabled if POS is off
 * @returns {Array<string>} List of optional dependent module keys
 */
export function getPOSDependencies() {
  return ['inventory', 'attendance'];
}

/**
 * Enforce module dependencies - if accountingCore is off, turn off dependents
 * @param {Object} enabledModules - Current enabledModules state
 * @param {string} moduleKey - Module being toggled
 * @param {boolean} newValue - New value for the module
 * @returns {Object} Updated enabledModules with dependencies enforced
 */
export function enforceModuleDependencies(enabledModules, moduleKey, newValue) {
  const updated = { ...enabledModules, [moduleKey]: newValue };

  // If accountingCore is being turned OFF, disable dependent modules
  if (moduleKey === 'accountingCore' && !newValue) {
    const dependencies = getAccountingCoreDependencies();
    dependencies.forEach(dep => {
      updated[dep] = false;
    });
  }

  // If accountingCore is being turned ON, don't force re-enable others
  // Let user decide individually

  return updated;
}

/**
 * Check if a module can be toggled given current state
 * @param {string} moduleKey - Module to check
 * @param {Object} enabledModules - Current enabledModules state
 * @returns {boolean} true if module can be toggled
 */
export function canToggleModule(moduleKey, enabledModules) {
  // Check if this module is dependent on another disabled module
  if (getAccountingCoreDependencies().includes(moduleKey)) {
    if (!enabledModules.accountingCore) {
      return false; // Can't enable dependent if accountingCore is off
    }
  }

  return true;
}

/**
 * Get the reason why a module is disabled (for tooltip/help text)
 * @param {string} moduleKey - Module to check
 * @param {Object} enabledModules - Current enabledModules state
 * @returns {string|null} Reason why disabled, or null if enabled
 */
export function getModuleDisabledReason(moduleKey, enabledModules) {
  if (getAccountingCoreDependencies().includes(moduleKey)) {
    if (!enabledModules.accountingCore) {
      return 'This module requires Accounting Core to be enabled';
    }
  }

  return null;
}